import React from "react";
import { styled } from "@mui/material/styles";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import { OutlinedInputProps } from "@mui/material/OutlinedInput";
import { IconButton, InputAdornment } from "@mui/material";
import { FmIcon } from "..";

import { useTheme } from "../../Hooks";

type FmTextFieldProps = TextFieldProps & {
    additionalInputProps?: any;
    inputProps?: any;
    textArea?: boolean;
    displayIcon?: boolean;
    onIconClick: () => void;
};

const FmTextField = styled(
    React.forwardRef<any, FmTextFieldProps>((props, ref) => {
        const { icon, fullWidth } = props.inputProps || {
            icon: undefined,
        };
        const { displayIcon, onIconClick, additionalInputProps, ...inputProps } = props;

        let withIcon = icon;
        let errorIcon = props.error;
        const theme = useTheme();

        if (errorIcon && !displayIcon) {
            withIcon =
                inputProps.name === "email" ? (
                    <FmIcon url={theme.images.error} title={"visible"} pointer={false} />
                ) : (
                    <FmIcon url={theme.images.error} title={"visible"} />
                );
        }

        errorIcon = false;

        const inputAdditionalProps = additionalInputProps || {};
        const inputPropsData = {
            ...(inputAdditionalProps.onChange ? { onChange: inputAdditionalProps.onChange } : {}),
            ...(inputAdditionalProps.onKeyDown ? { onKeyDown: inputAdditionalProps.onKeyDown } : {}),
            ...(inputAdditionalProps.onBlur ? { onBlur: inputAdditionalProps.onBlur } : {}),
        };

        return withIcon ? (
            <TextField
                type={props.type}
                inputRef={ref}
                spellCheck={false}
                autoComplete='true'
                fullWidth={true}
                InputProps={
                    {
                        ...inputAdditionalProps,
                        disableUnderline: true,
                        endAdornment: (
                            <InputAdornment position='end' sx={{ marginTop: "0px" }}>
                                {inputProps.name === "email" ? (
                                    <span
                                        style={{
                                            cursor: "auto",
                                            marginTop: "10px",
                                        }}
                                    >
                                        {withIcon}
                                    </span>
                                ) : (
                                    <IconButton onClick={onIconClick}>{withIcon}</IconButton>
                                )}
                            </InputAdornment>
                        ),
                        ...inputPropsData,
                    } as Partial<OutlinedInputProps>
                }
                {...inputProps}
            />
        ) : (
            <TextField
                type={props.type}
                inputRef={ref}
                spellCheck={false}
                autoComplete='true'
                fullWidth={fullWidth}
                InputProps={
                    {
                        ...inputAdditionalProps,
                        disableUnderline: true,
                        ...inputPropsData,
                    } as Partial<OutlinedInputProps>
                }
                {...inputProps}
            />
        );
    })
)(({ theme, textArea }: any) => ({
    ".MuiIconButton-root": {
        "&:hover": {
            backgroundColor: "transparent",
        },
    },
    ".MuiInputBase-root": {
        minHeight: "56px",
        paddingBottom: "10px",
    },
    "& .MuiFormHelperText-root": {
        ...(theme.direction === "ltr" ? { marginLeft: "2px" } : { marginRight: "2px" }),
        fontSize: "14px",
        color: "#BD0202",
        position: "absolute",
        ...(textArea ? { top: "158px" } : { top: "52px" }),
    },
    "& .Mui-error ~ .MuiFilledInput-root ": {
        backgroundColor: "#FEE8E8",
        boxShadow: "none",
        "&:hover, &:not(:hover) ": {
            backgroundColor: "#FEE8E8",
        },
    },

    "& .MuiInputLabel-root": {
        color: "#424242",
        opacity: "0.5",
        fontSize: "18px",
        letterSpacing: 0,
        backgroundColor: "transparent",
        position: "absolute",
        ...(theme.direction === "rtl" ? { right: 25 } : { left: 0 }),
    },

    "& .MuiInputLabel-shrink": {
        color: theme.palette.primary.main,
        fontSize: "18px",
        fontWeight: "bold",
        letterSpacing: 0,
        opacity: "1",
        backgroundColor: "transparent",
        marginTop: "0px",
        overflow: "visible ",
        position: "absolute",
        ...(theme.direction === "rtl" ? { right: -75 } : { left: 0 }),
    },

    "& .MuiFilledInput-root": {
        paddingBottom: "0px",
        border: "none",
        overflow: "hidden",
        borderRadius: "6px",
        backgroundColor: "#FFF",
        letterSpacing: 0,
        boxShadow: (theme as any).boxShadow.primary,
        transition: theme.transitions.create(["border-color", "background-color", "box-shadow"]),

        "& .MuiInputBase-input": {},
        "&:hover": {
            backgroundColor: "#FFF",
        },

        "&:not(:hover)": {
            backgroundColor: "#FFF",
        },

        "&:focus": {
            backgroundColor: "#FFF",
        },

        "& .Mui-disabled": {
            backgroundColor: "#F5F7FB",
            "&:hover": {
                backgroundColor: "#F5F7FB",
            },
        },

        "& .Mui-focused": {
            backgroundColor: "#FFF",
            boxShadow: (theme as any).boxShadow.primary,
            border: "0.56px solid #00833E",
        },
    },
}));

export default FmTextField;
